// Extracts a human-readable, design-system-safe message from an RTK Query
// error. Returns `undefined` when the server message is technical noise
// (Nest default 404 «Cannot METHOD /path», bare HTTP statuses) — callers
// should fall back to their own user-facing copy.

interface MaybeRtkError {
	data?: { message?: string | string[] }
	status?: number
	error?: string
}

function isTechnicalNoise(message: string): boolean {
	if (/^Cannot (GET|POST|PUT|PATCH|DELETE)\s/i.test(message)) return true
	if (/^Internal server error$/i.test(message)) return true
	if (/^Bad Request$/i.test(message)) return true
	if (/^Not Found$/i.test(message)) return true
	if (/^Unauthorized$/i.test(message)) return true
	if (/^Forbidden$/i.test(message)) return true
	return false
}

export function extractErrorMessage(err: unknown): string | undefined {
	if (!err) return undefined
	const message = (err as MaybeRtkError)?.data?.message
	// class-validator's ValidationPipe returns one untranslated English message
	// per failed field as a string[]. Raw English copy is not ideal, but several
	// dialogs gate their error alert on message presence with no fallback, so
	// suppressing the array would make gateway validation 400s fully silent
	// there — worse than showing raw text. The proper fix is a localized
	// exceptionFactory in the gateway (tracked as a follow-up issue).
	const candidate =
		typeof err === 'string'                           ? err :
		Array.isArray(message)                            ? message.join(', ') :
		typeof message === 'string'                       ? message :
		typeof (err as MaybeRtkError)?.error === 'string'  ? (err as MaybeRtkError).error as string :
		undefined
	if (!candidate || isTechnicalNoise(candidate)) return undefined
	return candidate
}
